/**
 * `@vaarta/suite-launcher/core` — everything the app grid decides, with no React in it.
 *
 * ⭐ THE DEFECT THIS CLOSES. Measured 2026-09-21 across the four suite applications: the
 * launcher exists **four times**.
 *
 *   | application       | where                                                          |
 *   | Analytics         | `features/analytics/shell/SuiteLauncher.tsx` (its own component) |
 *   | Automation Studio | inline in `components/layout/AppShell.tsx` (avatar dropdown)     |
 *   | Converse          | inline in `components/chatbot/SidebarUserMenu.tsx`               |
 *   | Data Studio       | inline in `components/layout/AppShell.tsx`                       |
 *
 * and `readSuiteTiles` / `safeLaunchUrl` / `launcherIsWorthShowing` are **byte-identical in
 * all four** `src/lib/suite-sso.ts` (Analytics differs in one comment and nothing else).
 * That is the AG-1583 shape exactly: four copies of one decision, which drifted into two
 * answers last time and shipped an Identity Studio page to the users of two products for a
 * week. Three more applications are joining — PPT Builder, App Builder, File Manager — so
 * the count was about to become seven.
 *
 * ⚠️ WHY A NEW PACKAGE AND NOT `@vaarta/auth-flow`. auth-flow's own package.json says what
 * it is in one line: *"Framework-agnostic, no React, no Next."* Its module docstring says
 * everything in it except `fetchFlowPlan` is pure, specifically so it can be tested on a
 * node environment with no `window`. A React component with a `peerDependencies` entry on
 * `react` would end that property for all four consumers, who import auth-flow for sign-in
 * logic and must keep being able to. So the launcher is a **sibling** package next to
 * `auth-flow` and `vaarta-iam`, distributed the same way (`file:` vendoring, no registry
 * for `@vaarta/*` yet), and split in two here: this file is pure and testable anywhere,
 * `index.tsx` is the panel.
 *
 * ⛔ NO APPLICATION IS NAMED ANYWHERE IN THIS PACKAGE, and that is a tested property, not a
 * convention (see `core.test.ts`, `no application is named in the package source`). The
 * whole point of the exercise is that PPT Builder, App Builder and File Manager appear by
 * being registered in Identity Studio, with zero edits here — so a per-app icon map, a
 * per-app category map or a per-app display-name fix would each silently reintroduce the
 * thing being removed. `monogram` and `tileAccent` derive a tile's artwork from its name,
 * which is why a seventh application gets a correct-looking tile for free.
 *
 * ⛔ NO ENTITLEMENT VOCABULARY, EITHER. `GET /app/{key}/sso/launcher` answers `allowed`
 * only — may this human open this application. `entitled` (has their organisation bought
 * it) is deliberately ABSENT rather than null, because entitlements currently fail open
 * (AG-1537) and answering it would mean crossing the measured billing boundary (AG-1317).
 * So there is no buy state, no upgrade state and no locked state to render, and inventing
 * one would make this the only thing in the estate that claimed to know. `core.test.ts`
 * pins the vocabulary out of both the source and the rendered markup.
 */
/**
 * Promoted verbatim from `src/lib/suite-sso.ts`, where four identical copies live.
 *
 * Keeps origin + path and drops query and fragment, and answers `null` for anything that is
 * not an absolute http(s) URL — a launcher tile is a place the browser is about to be sent,
 * so a value that is not a parseable absolute URL is not a destination.
 */
export function safeLaunchUrl(value) {
    const raw = typeof value === 'string' ? value.trim() : '';
    if (!raw)
        return null;
    try {
        const parsed = new URL(raw);
        if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:')
            return null;
        return parsed.origin + (parsed.pathname === '/' ? '' : parsed.pathname);
    }
    catch {
        return null;
    }
}
/**
 * Read the launcher response into tiles. Promoted from the four identical copies.
 *
 * ⚠️ `body.shared !== true` answers `[]` rather than throwing: a browser with no suite
 * cookie gets `{"shared": false, "applications": []}` from the route, which is a state and
 * not a fault. The component distinguishes the two — see `LauncherState` — because "you
 * signed in to this application directly" and "we could not ask" must not read alike.
 */
export function readSuiteTiles(payload) {
    if (!payload || typeof payload !== 'object')
        return [];
    const body = payload;
    if (body.shared !== true)
        return [];
    if (!Array.isArray(body.applications))
        return [];
    const tiles = [];
    for (const entry of body.applications) {
        if (!entry || typeof entry !== 'object')
            continue;
        const row = entry;
        const name = typeof row.name === 'string' ? row.name.trim() : '';
        // A nameless tile is not renderable, and inventing "Application" for it would put a
        // tile on screen that nobody can identify.
        if (!name)
            continue;
        const category = typeof row.category === 'string' ? row.category.trim() : '';
        tiles.push({
            name,
            launchUrl: safeLaunchUrl(row.launch_url),
            logoUrl: safeLaunchUrl(row.logo_url),
            current: row.current === true,
            mfaRequired: row.mfa_required === true,
            category: category || null,
        });
    }
    return tiles;
}
/**
 * Whether the person holds anything other than the application they are looking at.
 *
 * Promoted from the four copies, where it gated the whole control: a switcher that switches
 * to here is furniture. ⚠️ It no longer gates the TRIGGER — see `index.tsx`, "why the grid
 * icon is always there" — but it still decides whether the panel shows a grid or the honest
 * "nothing else here" line, so the decision survives rather than being deleted.
 */
export function launcherIsWorthShowing(tiles) {
    return tiles.filter((t) => !t.current).length > 0;
}
/**
 * The letters on a tile that has no artwork.
 *
 * ⚠️ Measured 2026-09-21 on dev: `application_brandings.logo_url` is **NULL for all four**
 * registered applications. So "a grid of tiles with icons" had no icon data on the day it
 * was asked for, and the tempting fix — a `lucide` icon per known application — is exactly
 * the edit that would make a seventh application a code change. Initials derived from the
 * name are artwork that arrives with the data, and `logoUrl` still wins whenever Identity
 * Studio has one.
 *
 * One word gives one letter, two or more give two; anything non-alphanumeric is not a word.
 * Upper-cased, because a monogram is a monogram.
 *
 * ⚠️ `\p{M}` is in the word class, not only `\p{L}\p{N}`. Combining marks are neither
 * letters nor digits, so without it every Devanagari vowel sign reads as a word break and
 * `वार्ता संवाद` monograms to `वर` — two letters from inside the FIRST word, having split it
 * into three. This is a suite whose own name is transliterated Hindi; the products will be
 * named in it.
 */
export function monogram(name) {
    const words = name
        .split(/[^\p{L}\p{N}\p{M}]+/u)
        .filter(Boolean);
    if (words.length === 0)
        return '?';
    if (words.length === 1)
        return words[0].slice(0, 1).toUpperCase();
    return (words[0].slice(0, 1) + words[1].slice(0, 1)).toUpperCase();
}
/**
 * A stable hue for a tile with no artwork, derived from its name.
 *
 * Deterministic on purpose and in two directions: the same application is the same colour
 * on every page of every product in the suite, and a NEW application gets a colour without
 * anybody choosing one. A random or index-based hue would fail both — index-based would
 * also recolour every tile the day an application is added, which is the sort of change
 * that reads as a bug.
 *
 * FNV-1a over the code points, which is small, has no dependencies and spreads short
 * strings well enough for this. The 137 step keeps neighbouring hashes from landing on
 * neighbouring hues.
 */
export function tileAccent(name) {
    let hash = 0x811c9dc5;
    for (let i = 0; i < name.length; i += 1) {
        hash ^= name.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return (hash % 360) * 137 % 360;
}
/** Where tiles with no category of their own go, once some other tile has one. */
export const UNCATEGORISED_LABEL = 'Other';
/**
 * Tiles into the sections the panel renders.
 *
 * Three behaviours, and the first is the one that ships today:
 *
 * 1. **No tile has a category** — one group, `label: null`, order untouched. This is the
 *    state on dev right now and the panel renders no headings at all in it. A single
 *    heading over every tile in the suite is furniture.
 * 2. **Some tiles have categories** — a group per category in FIRST-APPEARANCE order, so
 *    the suite's own `sort_order` decides which section leads rather than the alphabet;
 *    then one `Other` group for the rest, always last.
 * 3. **Order within a group** is the order the backend sent, for the same reason.
 *
 * ⚠️ Pure, and it does not sort: `suite_applications` already orders by
 * `SuiteApp.sort_order` then `app_name`, and re-sorting here would quietly overrule an
 * operator who set that column.
 */
export function groupTiles(tiles) {
    const anyCategory = tiles.some((t) => t.category !== null);
    if (!anyCategory)
        return tiles.length ? [{ label: null, tiles: [...tiles] }] : [];
    const groups = [];
    const byLabel = new Map();
    const uncategorised = [];
    for (const tile of tiles) {
        if (tile.category === null) {
            uncategorised.push(tile);
            continue;
        }
        let group = byLabel.get(tile.category);
        if (!group) {
            group = { label: tile.category, tiles: [] };
            byLabel.set(tile.category, group);
            groups.push(group);
        }
        group.tiles.push(tile);
    }
    if (uncategorised.length)
        groups.push({ label: UNCATEGORISED_LABEL, tiles: uncategorised });
    return groups;
}
/**
 * Type-to-filter. Matches on the name and on the category, case- and accent-insensitively.
 *
 * Substring rather than prefix: somebody looking for "Data Studio" may well type "studio",
 * and with seven products two of them share a word. An empty or whitespace query is not a
 * filter and returns everything rather than nothing.
 */
export function filterTiles(tiles, query) {
    const needle = normaliseForSearch(query);
    if (!needle)
        return [...tiles];
    return tiles.filter((tile) => {
        const haystack = normaliseForSearch(`${tile.name} ${tile.category ?? ''}`);
        return haystack.includes(needle);
    });
}
function normaliseForSearch(value) {
    return value
        .normalize('NFD')
        // Strip combining marks, so "Vaarta" matches "Vāarta" and a pasted name with an accent
        // in it still finds its tile.
        .replace(/[̀-ͯ]/g, '')
        .trim()
        .toLowerCase();
}
/**
 * How many tiles it takes before a filter box earns its place.
 *
 * Seven, because seven is the number the suite is becoming — four today plus PPT Builder,
 * App Builder and File Manager. Below it a search box over a grid you can read at a glance
 * is one more thing to look at; at it, hunting starts. Overridable per mount, because the
 * threshold is a judgement and not a fact.
 */
export const FILTER_THRESHOLD = 7;
/** Whether to render the filter box for this many tiles. */
export function shouldShowFilter(tiles, threshold = FILTER_THRESHOLD) {
    return tiles.length >= threshold;
}
/**
 * An honest sentence for a request that did not produce a list.
 *
 * ⛔ Never "Something went wrong". Each of these has been a real state in this estate and
 * each has a different remedy, so each gets its own sentence:
 *
 * * **403** is the origin gate, not the person — `_check_domain_or_403` refuses a browser
 *   whose origin is not in the calling application's registered `allowed_domains`
 *   (AG-1845). The remedy is a registration, and a user reading "check your connection"
 *   would be hunting the wrong thing.
 * * **401** is the credential plane answering, which means a launcher is pointed at
 *   `/iam/sdk/me/applications` instead of `/app/{key}/sso/launcher`. That mistake is worth
 *   naming precisely, because it is the mistake this route exists to prevent.
 * * **429** is a rate limit and resolves itself.
 * * **5xx** is Identity Studio, which 502s its siblings for about a minute on every deploy.
 */
export function launcherErrorMessage(status) {
    if (status === 401) {
        return 'Identity Studio needs application credentials for this request, which a browser cannot hold. The launcher is pointed at the wrong endpoint.';
    }
    if (status === 403) {
        return 'Identity Studio would not answer this page’s origin. This application’s domains may need registering.';
    }
    if (status === 429) {
        return 'Identity Studio is rate-limiting this page. Try again in a moment.';
    }
    if (status >= 500) {
        return `Identity Studio is not answering right now (${status}). It is briefly unavailable during a deploy.`;
    }
    return `Identity Studio answered ${status}, which the launcher cannot read.`;
}
/** The sentence for a request that never got a status at all. */
export const UNREACHABLE_MESSAGE = 'Could not reach Identity Studio to list your applications.';
/**
 * Ask the launcher route, and turn whatever happens into a `LauncherState`.
 *
 * ⚠️ `credentials: 'include'` is the whole request. The suite cookie is what authenticates
 * it, it is HttpOnly so nothing here can read or forge it, and a cross-origin read is
 * possible only because Identity Studio derives its CORS allowlist from each application's
 * registered domains (AG-1845). ⛔ No bearer token and no app secret: a browser must never
 * hold the SDK credential, which is the entire reason this route exists beside
 * `/iam/sdk/me/applications`.
 *
 * `fetchImpl` is injectable so the tests never touch the network, matching how
 * `auth-flow.fetchFlowPlan` is built and tested.
 */
export async function fetchSuiteTiles(args) {
    const { url, fetchImpl, signal } = args;
    const doFetch = fetchImpl ?? (typeof fetch === 'function' ? fetch : undefined);
    if (!doFetch)
        return { status: 'error', message: UNREACHABLE_MESSAGE };
    let res;
    try {
        res = await doFetch(url, { credentials: 'include', signal });
    }
    catch {
        // Includes an abort, which the caller discards rather than renders.
        return { status: 'error', message: UNREACHABLE_MESSAGE };
    }
    if (!res.ok)
        return { status: 'error', message: launcherErrorMessage(res.status) };
    let body;
    try {
        body = await res.json();
    }
    catch {
        return { status: 'error', message: 'Identity Studio’s answer could not be read.' };
    }
    const shared = !!body && typeof body === 'object' && body.shared === true;
    return { status: 'ready', tiles: readSuiteTiles(body), shared };
}
/**
 * The tile's accessible name, composed rather than left to the DOM to assemble.
 *
 * ⭐ This is a function and not JSX text because the accessible name is the thing that gets
 * MEASURED. A previous session ran a source regex over tile markup, over-reported a defect
 * across four applications, and only a live a11y reading narrowed it to one page. A name
 * built here is asserted here, and the live reading checks the same string rather than a
 * different one.
 *
 * The parenthesised suffixes carry what the visible chips say, because a chip reading "2FA"
 * is three characters a screen reader cannot expand.
 */
export function tileAccessibleName(tile) {
    return [
        tile.name,
        tile.current ? '(current application)' : null,
        tile.mfaRequired && !tile.current ? '(two-factor sign-in required)' : null,
        !tile.launchUrl && !tile.current ? '(no address registered)' : null,
    ]
        .filter(Boolean)
        .join(' ');
}
/** Which of the three shapes a tile renders as. See `Tile` in `index.tsx` for why. */
export function tileKind(tile) {
    if (!tile.launchUrl)
        return 'inert';
    return tile.current ? 'current' : 'link';
}
/**
 * Which way the panel should open — AG-2391, measured on dev 2026-09-22.
 *
 * ⛔ THE DEFECT. The panel opened DOWNWARD unconditionally (`top: calc(100% + 8px)`), which
 * is right for a trigger in a top bar and wrong for Converse, whose launcher sits in the
 * SIDEBAR FOOTER. Opening downward from there put most of the panel below the fold: the
 * tiles were half-drawn and "You are here" was clipped off the bottom of the window.
 *
 * ⚠️ Decided from the TRIGGER's position at click time, before the panel renders, so there
 * is no flash of a wrongly-placed panel to correct. That is why this takes measurements
 * rather than a panel element.
 *
 * Down unless there is genuinely not room and up is roomier — a launcher in a top bar, which
 * is three of the four mounts, must not start flipping upward on a short window when down
 * still fits.
 */
export function preferredDrop(args) {
    const { spaceAbove, spaceBelow, desired } = args;
    if (spaceBelow >= desired)
        return 'down';
    return spaceAbove > spaceBelow ? 'up' : 'down';
}
/** What `preferredDrop` asks for when the caller has no better number. */
export const PANEL_DESIRED_HEIGHT = 320;
